import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    FaPaperPlane,
    FaSearch,
    FaInbox,
    FaReply,
    FaTrash,
    FaEye,
    FaTimes,
    FaUserCircle,
    FaCircle
} from 'react-icons/fa';
import DataTable from '../../SuperAdmin/shared/DataTable';
import Loader from '../../shared/Loader';
import { showSuccess, showError } from '../../../utils/Alert';
import { API_URL } from '../../../config';
import './RecruiterMessaging.css';

const RecruiterMessaging = () => {
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedThread, setSelectedThread] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [reply, setReply] = useState('');
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        fetchMessages();
    }, []);

    const fetchMessages = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get('/api/tutor/messages/inbox', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setMessages(res.data);
        } catch (err) {
            console.error("Messaging fetch error:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenReply = (msg) => {
        setSelectedThread(msg);
        setIsModalOpen(true);
        // Mark as read logic could go here
    };

    const handleSend = async (e) => {
        e.preventDefault();
        if (!reply.trim() || !selectedThread) return;

        try {
            const token = localStorage.getItem('token');
            await axios.post('/api/tutor/messages/send', {
                receiverId: selectedThread.senderId._id,
                content: reply,
                subject: `Re: ${selectedThread.subject || 'Inquiry'}`,
                parentMessageId: selectedThread._id
            }, { headers: { Authorization: `Bearer ${token}` } });

            setReply('');
            setIsModalOpen(false);
            showSuccess('Message Sent', 'Your reply has been delivered successfully.');
            fetchMessages();
        } catch (err) {
            showError('Send Failed', 'Could not deliver your message at this time.');
        }
    };

    const columns = [
        {
            header: 'Sender',
            key: 'senderId',
            render: (row) => (
                <div className="sender-cell">
                    <div className="avatar-small">
                        {row.senderId?.profileImage ? (
                            <img src={`${API_URL}${row.senderId.profileImage}`} alt="" />
                        ) : (
                            <div className="avatar-init-vsmall">{row.senderId?.name?.charAt(0).toUpperCase()}</div>
                        )}
                    </div>
                    <div className="sender-info">
                        <strong>{row.senderId?.name}</strong>
                        {!row.isRead && <span className="unread-label">UNREAD</span>}
                    </div>
                </div>
            )
        },
        {
            header: 'Subject & Preview',
            key: 'subject',
            render: (row) => (
                <div className="content-cell">
                    <span className="msg-subject">{row.subject || 'General Inquiry'}</span>
                    <p className="msg-preview">{row.content.substring(0, 60)}...</p>
                </div>
            )
        },
        {
            header: 'Received',
            key: 'createdAt',
            render: (row) => (
                <span className="date-cell">
                    {new Date(row.createdAt).toLocaleDateString()} at {new Date(row.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
            )
        },
        {
            header: 'Actions',
            key: 'actions',
            render: (row) => (
                <div className="action-btns-cell">
                    <button className="icon-btn-reply" onClick={() => handleOpenReply(row)} title="View & Reply">
                        <FaReply />
                    </button>
                </div>
            )
        }
    ];

    if (loading) return <Loader />;

    const filteredMessages = messages.filter(msg =>
        msg.senderId?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        msg.subject?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        msg.content?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="messaging-high-density fade-in">
            <div className="dashboard-header">
                <div>
                    <h1 className="page-title">Candidate Correspondence</h1>
                    <p className="page-subtitle">High-density overview of all incoming inquiries and coordination threads.</p>
                </div>
            </div>

            <div className="table-controls">
                <div className="search-bar-wrapper">
                    <FaSearch className="search-icon" />
                    <input
                        type="text"
                        placeholder="Search through correspondence..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>

            <div className="premium-card no-padding">
                <DataTable
                    columns={columns}
                    data={filteredMessages}
                    title="Communication Registry"
                />
            </div>

            {/* Reply Modal */}
            {isModalOpen && selectedThread && (
                <div className="modal-overlay">
                    <div className="modal-content reply-modal premium-card">
                        <div className="modal-header">
                            <div className="user-profile-header">
                                <div className="avatar-med">
                                    {selectedThread.senderId?.profileImage ? (
                                        <img src={`${API_URL}${selectedThread.senderId.profileImage}`} alt="" />
                                    ) : (
                                        <FaUserCircle />
                                    )}
                                </div>
                                <div>
                                    <h2>{selectedThread.senderId?.name}</h2>
                                    <span className="subject-meta">Subject: {selectedThread.subject || 'General Inquiry'}</span>
                                </div>
                            </div>
                            <button className="modal-close-btn" onClick={() => setIsModalOpen(false)}><FaTimes /></button>
                        </div>

                        <div className="modal-body">
                            <div className="original-message">
                                <div className="msg-meta-bar">
                                    <span>Received {new Date(selectedThread.createdAt).toLocaleString()}</span>
                                </div>
                                <div className="message-text">
                                    {selectedThread.content}
                                </div>
                            </div>

                            <form onSubmit={handleSend} className="reply-form">
                                <label>Write Professional Response</label>
                                <textarea
                                    value={reply}
                                    onChange={(e) => setReply(e.target.value)}
                                    placeholder="Type your message here..."
                                    rows="6"
                                    required
                                ></textarea>
                                <div className="form-actions">
                                    <button type="button" className="secondary-btn" onClick={() => setIsModalOpen(false)}>Dismiss</button>
                                    <button type="submit" className="primary-btn" disabled={!reply.trim()}>
                                        Send Message <FaPaperPlane />
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RecruiterMessaging;
